import React from 'react';
import { Link } from 'react-router-dom';
import {
  FaAtom,
  FaBrain,
  FaChartLine,
  FaCloud,
  FaCode,
  FaLightbulb,
  FaRocket,
  FaShieldAlt,
  FaUsers,
} from 'react-icons/fa';
import Navbar from '../components/Navbar';
import BottomContent from '../components/BottomContent';
import ScrollButton from "../components/ScrollButton";
import Thank from '../components/Thank';
import AboutImg from '../assets/img/AboutImg.jpg';
import vedaLogo from '../assets/logo/veda.png';
import DarkModeButton from '../components/DarkModeButton';
import ContactButton from '../components/ContactButton';
import './About.css';

const leaders = [
  { name: 'Mr. Ramakant Roy', role: 'Co-founder and Director' },
  { name: 'Mr. Aditya Kumar', role: 'Director (Operation)' },
  { name: 'Dr. Gajendra Prasad Singh', role: 'Director (Legal)' },
  { name: 'Mr. Kapil Kale', role: 'Director (Sales & Marketing)' },
  { name: 'Dr. Sandhya Singh Yadav', role: 'Director (Molecular Science)' },
];

const capabilities = [
  {
    icon: <FaBrain />,
    title: 'AI Innovation',
    text: 'Applied AI, generative intelligence, automation, and decision-support systems built for practical business outcomes.',
  },
  {
    icon: <FaCode />,
    title: 'Digital Engineering',
    text: 'Modern web, application, cloud, DevOps, and platform engineering for scalable digital experiences.',
  },
  {
    icon: <FaAtom />,
    title: 'Scientific AI',
    text: 'Molecular discovery, data-driven research workflows, and advanced analytics for science-led organizations.',
  },
  {
    icon: <FaShieldAlt />,
    title: 'Secure Solutions',
    text: 'Security-aware architecture, authentication, privacy-minded design, and enterprise-ready implementation.',
  },
];

const stats = [
  { value: 'AI', label: 'First product thinking' },
  { value: 'R&D', label: 'Driven innovation' },
  { value: '360', label: 'Digital transformation support' },
];

const values = [
  'Build useful technology, not noise',
  'Blend research depth with delivery discipline',
  'Design for adoption, scale, and clarity',
  'Work as long-term innovation partners',
];

export default function About() {
  return (
    <>
      <Navbar />

      <main className="about-page">
        <section className="about-hero" style={{ backgroundImage: `url(${AboutImg})` }}>
          <div className="about-hero-overlay"></div>
          <div className="container about-hero-inner">
            <div className="about-hero-copy">
              <div className="about-brand-chip">
                <img src={vedaLogo} alt="Vedalytics" />
                <span>Vedalytics</span>
              </div>
              <h1>About Vedalytics</h1>
              <h2>Welcome to the Future of Development & Technology.</h2>
              <p>
                At Vedalytics, we turn visionary ideas into innovative solutions,
                blending creativity and technology to deliver exceptional results.
                Our mission is to craft dynamic, impactful digital experiences that
                build lasting brands online.
              </p>
              <div className="about-hero-actions">
                <Link to="/scheduleDemo" className="about-btn about-btn-primary">
                  Schedule Demo
                </Link>
                <Link to="/contact" className="about-btn about-btn-secondary">
                  Contact Us
                </Link>
              </div>
            </div>

            <div className="about-vision-panel">
              <span className="about-panel-label">What we create</span>
              <div className="about-orbit">
                <FaRocket />
                <strong>Digital products with intelligent foundations</strong>
              </div>
              <div className="about-panel-list">
                <div><FaCloud /> Cloud-ready platforms</div>
                <div><FaChartLine /> Data-led decisions</div>
                <div><FaLightbulb /> Research-driven products</div>
              </div>
            </div>
          </div>
        </section>

        <section className="about-section about-story-section">
          <div className="container">
            <div className="row align-items-center gy-5">
              <div className="col-lg-5">
                <span className="about-eyebrow">Who we are</span>
                <h2>We are a technology partner for teams building what comes next.</h2>
              </div>
              <div className="col-lg-7">
                <p>
                  Vedalytics brings together digital strategy, software engineering,
                  AI research, and product thinking to help organizations modernize
                  operations, create intelligent products, and move faster with
                  confidence. From enterprise transformation to specialized AI
                  platforms, our work is shaped around clarity, usability, and
                  measurable impact.
                </p>
              </div>
            </div>

            <div className="about-stat-grid">
              {stats.map((stat) => (
                <div className="about-stat-card" key={stat.label}>
                  <strong>{stat.value}</strong>
                  <span>{stat.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="about-section about-capabilities-section">
          <div className="container">
            <div className="about-section-heading">
              <span className="about-eyebrow">What we do</span>
              <h2>Technology, intelligence, and execution in one connected practice.</h2>
            </div>

            <div className="row g-4">
              {capabilities.map((item) => (
                <div className="col-md-6 col-xl-3" key={item.title}>
                  <article className="about-capability-card">
                    <div className="about-capability-icon">{item.icon}</div>
                    <h3>{item.title}</h3>
                    <p>{item.text}</p>
                  </article>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="about-section about-values-section">
          <div className="container">
            <div className="row align-items-center gy-4">
              <div className="col-lg-5">
                <span className="about-eyebrow">Our approach</span>
                <h2>Practical innovation with a clear delivery mindset.</h2>
                <p>
                  We believe strong technology should feel purposeful: easy to use,
                  reliable to operate, and meaningful to the people it serves.
                </p>
              </div>
              <div className="col-lg-7">
                <div className="about-value-list">
                  {values.map((value, index) => (
                    <div className="about-value-item" key={value}>
                      <span>{String(index + 1).padStart(2, '0')}</span>
                      <p>{value}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="about-section about-team-section">
          <div className="container">
            <div className="about-section-heading">
              <span className="about-eyebrow">Meet our team</span>
              <h2>Leadership across technology, operations, growth, legal, and science.</h2>
            </div>

            <div className="about-team-grid">
              {leaders.map((leader) => (
                <article className="about-leader-card" key={leader.name}>
                  <div className="about-avatar">
                    <FaUsers />
                  </div>
                  <h3>{leader.name}</h3>
                  <p>{leader.role}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="about-cta-section">
          <div className="container">
            <div className="about-cta-panel">
              <div>
                <span className="about-eyebrow">Having Projects?</span>
                <h2>Schedule a free demo and start building the business you always imagined.</h2>
              </div>
              <Link to="/scheduleDemo" className="about-btn about-btn-primary">
                Schedule Demo
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Thank />
      <BottomContent />
      <ContactButton />
      <DarkModeButton />
      <ScrollButton />
    </>
  );
}
